export const RELEASE_FEATURE_FLAGS = {
  memberProfiles: 'release-member-profiles',
  profileEditing: 'release-profile-editing',
  profilePhotos: 'release-profile-photos',
  eventDescription: 'release-event-description',
  eventMedia: 'release-event-media',
  registrationForms: 'release-registration-forms',
  eventPayments: 'release-event-payments',
  receiptUploads: 'release-receipt-uploads',
  paymentReview: 'release-payment-review',
  rsvp: 'release-rsvp',
  seatReservations: 'release-seat-reservations',
  eventReminders: 'release-event-reminders',
  eventCheckOut: 'release-event-check-out',
  routeLibrary: 'release-route-library',
  routePlanning: 'release-route-planning',
  routeSearch: 'release-route-search',
  routePreviews: 'release-route-previews',
  gpxImport: 'release-gpx-import',
  routeSharing: 'release-route-sharing',
  contactCapture: 'release-contact-capture',
  friendConnections: 'release-friend-connections',
  calendarExport: 'release-calendar-export',
  calendarSync: 'release-calendar-sync',
  stravaIntegration: 'release-strava-integration',
  instagramIntegration: 'release-instagram-integration',
  eventWaitlisting: 'release-event-waitlisting',
  contactManagement: 'release-contact-management',
  paymentManagement: 'release-payment-management',
  broadcastMessaging: 'release-broadcast-messaging',
  integrationSettings: 'release-integration-settings',
  eventDraftPublish: 'release-event-draft-publish',
} as const;

export type ReleaseFeature = keyof typeof RELEASE_FEATURE_FLAGS;
export type ReleaseFeatureFlagKey = (typeof RELEASE_FEATURE_FLAGS)[ReleaseFeature];

export const RELEASE_FEATURE_FLAG_MANIFEST = [
  ['memberProfiles', 'Member profiles'],
  ['profileEditing', 'Profile editing'],
  ['profilePhotos', 'Profile photos'],
  ['eventDescription', 'Event description'],
  ['eventMedia', 'Event media'],
  ['registrationForms', 'Registration forms'],
  ['eventPayments', 'Event payments'],
  ['receiptUploads', 'Receipt uploads'],
  ['paymentReview', 'Payment review'],
  ['rsvp', 'RSVP'],
  ['seatReservations', 'Seat reservations'],
  ['eventReminders', 'Event reminders'],
  ['eventCheckOut', 'Event check-out'],
  ['routeLibrary', 'Route library'],
  ['routePlanning', 'Route planning'],
  ['routeSearch', 'Route search'],
  ['routePreviews', 'Route previews'],
  ['gpxImport', 'GPX import'],
  ['routeSharing', 'Route sharing'],
  ['contactCapture', 'Contact capture'],
  ['friendConnections', 'Friend connections'],
  ['calendarExport', 'Calendar export'],
  ['calendarSync', 'Calendar sync'],
  ['stravaIntegration', 'Strava integration'],
  ['instagramIntegration', 'Instagram integration'],
  ['eventWaitlisting', 'Event waitlisting'],
  ['contactManagement', 'Contact management'],
  ['paymentManagement', 'Payment management'],
  ['broadcastMessaging', 'Broadcast messaging'],
  ['integrationSettings', 'Integration settings'],
  ['eventDraftPublish', 'Event draft or publish'],
] as const satisfies readonly (readonly [ReleaseFeature, string])[];

/**
 * Native API routes cannot safely evaluate a client-targeted PostHog flag.
 * This remains a server-side kill switch in addition to the PostHog release flag.
 */
export const PAID_EVENTS_SERVER_ENABLED = process.env.EXPO_PUBLIC_ENABLE_PAID_EVENTS === 'true';

export function resolveReleaseFeatureFlag(
  remoteValue: boolean | undefined,
  localDevelopmentValue?: boolean
): boolean | undefined {
  return localDevelopmentValue ?? remoteValue;
}

export function requiredReleaseFeaturesForPathname(pathname: string): readonly ReleaseFeature[] {
  if (pathname.startsWith('/app/members/')) return ['memberProfiles'];
  if (pathname === '/app/profile/edit' || /^\/admin\/[^/]+\/profile\/edit$/.test(pathname)) {
    return ['profileEditing'];
  }
  if (pathname === '/app/routes/new') return ['routePlanning'];
  if (pathname === '/app/routes/import-guide') return ['gpxImport'];
  if (pathname === '/app/routes' || pathname.startsWith('/app/routes/')) {
    return ['routeLibrary'];
  }
  if (pathname.startsWith('/routes/')) return ['routeSharing'];
  if (pathname === '/app/notifications') return ['eventReminders'];
  if (/^\/admin\/[^/]+\/contacts(?:\/|$)/.test(pathname)) return ['contactManagement'];
  if (/^\/admin\/[^/]+\/broadcasts(?:\/|$)/.test(pathname)) {
    return ['broadcastMessaging'];
  }

  const isIntegrationSettings =
    pathname === '/app/integrations' ||
    pathname.startsWith('/app/integrations/') ||
    pathname.startsWith('/app/settings/integrations') ||
    /^\/admin\/[^/]+\/(?:settings\/)?integrations(?:\/|$)/.test(pathname);
  if (isIntegrationSettings && pathname.endsWith('/instagram')) {
    return ['integrationSettings', 'instagramIntegration'];
  }
  if (isIntegrationSettings) return ['integrationSettings'];

  return [];
}
